/**
 * Maps Worker JSON error codes to demo-safe copy (no paths / stack details).
 * Aligned with BACKLOG product P0 and S7 posture (avoid raw codes in chrome when we can).
 */

const HTTP_ERROR_MESSAGES: Record<string, string> = {
	not_found: "This poll link is missing or expired. Ask the curator for a current link.",
	invalid_room_id: "That poll link isn’t valid. Check the URL and try again.",
	invalid_seal:
		"This Curator Link doesn’t match the poll (typo or wrong link). Find the link you saved when the poll was created.",
	curator_auth_required: "Curator session expired. Open your Curator Link again to continue.",
	unauthorized: "You don’t have access to do that.",
	room_header_mismatch: "Something got out of sync. Refresh the page and try again.",
	bad_phase: "That action isn’t available for this poll right now. Refresh if the curator just changed the phase.",
	vote_kind_mismatch: "This poll’s voting style changed. Refresh the page.",
	invalid_ranked_ballot: "That ranking isn’t valid for this poll. Make sure each idea appears once, then cast again.",
	quadratic_over_budget_or_invalid:
		"Those vote counts don’t fit the quadratic budget (v² totals must stay within the limit). Adjust and try again.",
	rate_limited: "Too many polls were created from this network. Wait a bit and try again.",
	server_misconfigured: "The service isn’t configured correctly. Try again later or contact support.",
	billing_not_configured: "Membership checkout isn’t available yet.",
	checkout_failed: "Checkout couldn’t start. Wait a moment and try again.",
	invalid_session: "That checkout session isn’t valid. Start checkout again from Membership.",
	session_not_paid: "Payment hasn’t completed yet. Finish checkout or try again.",
	missing_customer: "We couldn’t tie this payment to an account. Contact support if this persists.",
	invalid_json: "The request couldn’t be read. Refresh and try again.",
	validation_failed: "Something in the form didn’t validate. Review the fields and try again.",
	missing_signature: "We couldn’t verify that request. Try again or contact support.",
	invalid_signature: "We couldn’t verify that request. Try again or contact support.",
	webhook_processing_failed: "Something went wrong completing that step. Try again in a moment.",
	turnstile_required:
		"Complete the quick check challenge below (not the curator link box), then try launching the poll again.",
	turnstile_failed: "Verification didn’t go through. Refresh the page and try again.",
};
const REALTIME_ERROR_MESSAGES: Record<string, string> = {
	...HTTP_ERROR_MESSAGES,
	forbidden: "You can’t perform that action in this role.",
	missing_voter_id: "Connection dropped. Refresh the page, then cast your vote again.",
	snapshot_failed: "Live updates hiccuped. Refresh to see the latest state.",
	finalize_failed: "Finalizing didn’t complete. Refresh and try again.",
	invalid_message: "That command wasn’t recognized. Refresh and try again.",
	vote_rejected: "That vote couldn’t be recorded. Adjust your ballot and try again.",
};

function looksSnakeCaseToken(s: string): boolean {
	return /^[a-z][a-z0-9_]*$/.test(s) && s.includes("_");
}

function titleFromSnake(s: string): string {
	return s
		.split("_")
		.map((w) => w.slice(0, 1).toUpperCase() + w.slice(1))
		.join(" ");
}

/**
 * Response body from `fetch().json()` when `!res.ok`.
 */
export function friendlyApiErrorPayload(data: unknown, fallback = "Something went wrong. Please try again."): string {
	if (!data || typeof data !== "object") return fallback;
	const o = data as Record<string, unknown>;
	const code = typeof o.error === "string" ? o.error : null;
	const message = typeof o.message === "string" ? o.message : null;

	if (code && HTTP_ERROR_MESSAGES[code]) {
		if (code === "rate_limited") {
			const retryRaw = o.retryAfterSec;
			const retryAfterSec = typeof retryRaw === "number" && Number.isFinite(retryRaw) && retryRaw > 0 ? retryRaw : null;
			if (retryAfterSec != null) {
				if (retryAfterSec <= 120) {
					return `Too many polls were created from this network. Try again in about ${Math.ceil(retryAfterSec)} seconds.`;
				}
				const mins = Math.max(1, Math.ceil(retryAfterSec / 60));
				return `Too many polls were created from this network. Try again in about ${mins} minutes.`;
			}
			return message && message.trim().length > 0 ? message : HTTP_ERROR_MESSAGES.rate_limited;
		}
		return HTTP_ERROR_MESSAGES[code];
	}

	if (code && looksSnakeCaseToken(code)) return titleFromSnake(code);

	// Omit arbitrary server `message`, `detail`, and Zod `issues` — they may leak paths or schema internals.
	return fallback;
}

/** Maps DO-authored WebSocket `message` strings to narrative-safe copy. */
const WS_DIAGNOSTIC_PHRASES: Record<string, string> = {
	"Voters only": REALTIME_ERROR_MESSAGES.forbidden,
	"Curator only": REALTIME_ERROR_MESSAGES.forbidden,
	"Voting is not open": HTTP_ERROR_MESSAGES.bad_phase,
	"Can only open voting while collecting": HTTP_ERROR_MESSAGES.bad_phase,
	"Can only finalize while voting": HTTP_ERROR_MESSAGES.bad_phase,
	"Reconnect and send hello first": REALTIME_ERROR_MESSAGES.missing_voter_id,
	"Unknown message shape": REALTIME_ERROR_MESSAGES.invalid_message,
};

/** Messages from curator/voter WebSocket `error` frames (often error code strings from internal HTTP). */
export function friendlyRealtimeErrorMessage(raw: string): string {
	const t = raw.trim();
	if (!t) return "Something went wrong. Please try again.";

	if (t.startsWith("{") || t.startsWith("[")) {
		try {
			const j = JSON.parse(t) as { error?: string };
			if (typeof j.error === "string") return friendlyRealtimeErrorMessage(j.error);
		} catch {
			/* ignore */
		}
		return REALTIME_ERROR_MESSAGES.vote_rejected;
	}

	const direct = REALTIME_ERROR_MESSAGES[t];
	if (direct) return direct;

	const lower = t.toLowerCase();
	for (const [code, msg] of Object.entries(REALTIME_ERROR_MESSAGES)) {
		if (lower === code) return msg;
	}

	// Brief server-authored phrases used by the Room DO (safe copy, no raw tokens).
	const wsPhrase = WS_DIAGNOSTIC_PHRASES[t];
	if (wsPhrase) return wsPhrase;

	if (looksSnakeCaseToken(t)) return titleFromSnake(t);

	return fallbackFromToken(t);
}

function fallbackFromToken(t: string): string {
	return looksSnakeCaseToken(t) ? titleFromSnake(t) : REALTIME_ERROR_MESSAGES.vote_rejected;
}

export type CompanionBallotHost = "slack" | "clickup" | "asana" | "teams";

/** Client fallback when companion ballot verify returns no server `message`. */
export function companionBallotVerifyFallback(host: CompanionBallotHost): string {
	if (host === "clickup") {
		return "This ballot link is invalid or expired. Request a fresh vote link from the ClickUp task.";
	}
	if (host === "asana") {
		return "This ballot link is invalid or expired. Request a fresh vote link from the Asana task.";
	}
	if (host === "teams") {
		return "This ballot link is invalid or expired. Request a fresh vote link from the Teams conversation.";
	}
	return "This ballot link is invalid or expired. Open the poll in Slack and tap Cast Your Vote again.";
}

export function companionBallotEyebrow(host: CompanionBallotHost): string {
	if (host === "clickup") return "ClickUp ballot";
	if (host === "asana") return "Asana ballot";
	if (host === "teams") return "Teams ballot";
	return "Slack ballot";
}

export function companionHostLiveBadge(host: CompanionBallotHost): string {
	if (host === "clickup") return "ClickUp";
	if (host === "asana") return "Asana";
	if (host === "teams") return "Teams";
	return "Slack";
}
